import { trendTextColor } from './mapColorScales';
import { gradeBandChildCopy } from './newarkMapConfig';

const FONT = "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";

export const formatInt = (value) =>
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? 'N/A'
    : Math.round(Number(value)).toLocaleString();

export const formatPct = (value, digits = 0) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return 'N/A';
  return `${(Number(value) * 100).toFixed(digits)}%`;
};

export const formatSignedPct = (value, digits = 0) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '';
  const pct = Number(value) * 100;
  const body = `${pct >= 0 ? '+' : ''}${pct.toFixed(digits)}%`;
  return `(${body})`;
};

export function formatAreaMi2(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return 'N/A';
  return `${Number(value).toFixed(1)} mi²`;
}

export function populationChange(current, future) {
  if (
    current === null ||
    current === undefined ||
    future === null ||
    future === undefined ||
    !Number.isFinite(Number(current)) ||
    !Number.isFinite(Number(future)) ||
    Number(current) === 0
  ) {
    return null;
  }
  return (Number(future) - Number(current)) / Number(current);
}

export function childPopTrendDisplay(current, future) {
  const change = populationChange(current, future);
  if (change === null) return { text: 'N/A', color: '#888888' };
  const pct = Math.round(Math.abs(change) * 100);
  if (change > 0.005) return { text: `▲ ${pct}%`, color: '#2a9d2a' };
  if (change < -0.005) return { text: `▼ ${pct}%`, color: '#cc2222' };
  return { text: `${pct}%`, color: '#888888' };
}

export const isYes = (value) => String(value || '').toLowerCase() === 'yes';

export function formatGrades(raw) {
  if (!raw) return 'N/A';
  return String(raw)
    .split(',')
    .map((part) =>
      part
        .trim()
        .replace(/^Kindergarten$/i, 'K')
        .replace(/^Grade\s+/i, '')
    )
    .join(', ');
}

export function formatAddress(school) {
  const street = (school?.street || '').trim();
  const city = (school?.city || '').trim();
  if (street && city) return `${street}, ${city}`;
  return street || city || '';
}

export function vsStateLabel(flag) {
  if (!flag) return { text: 'N/A', color: '#888888' };
  const normalized = String(flag).toLowerCase();
  if (normalized === 'above' || normalized === 'at or above') {
    return { text: 'At or Above', color: '#2a9d2a' };
  }
  if (normalized === 'below') return { text: 'Below', color: '#cc2222' };
  return { text: flag, color: '#888888' };
}

export function vsStateFromBar(value, bar) {
  if (value === null || value === undefined || bar === null || bar === undefined) {
    return { text: 'N/A', color: '#888888' };
  }
  return Number(value) >= Number(bar)
    ? { text: 'At or Above', color: '#2a9d2a' }
    : { text: 'Below', color: '#cc2222' };
}

function tile(label, value, sub, { span2 = false, valueColor = '#111' } = {}) {
  const span = span2 ? 'grid-column:span 2;' : '';
  const subHtml = sub
    ? `<div style="font-size:10px;font-weight:700;color:${sub.color};">${esc(sub.text)}</div>`
    : '';
  return `<div style="background:#f7f8fa;border-radius:6px;padding:7px 9px;${span}">
    <div style="font-size:10px;color:#999;margin-bottom:2px;">${esc(label)}</div>
    <div style="font-size:${span2 ? 15 : 14}px;font-weight:700;color:${valueColor};">${esc(value)}</div>
    ${subHtml}
  </div>`;
}

function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function trendLine(status, pct) {
  const label = status || 'No data';
  const signed = formatSignedPct(pct);
  return `→ ${label}${signed ? ` ${signed}` : ''}`;
}

/** "Declining -8%" without the arrow or parentheses, for one-line hover rows. */
export function shortTrend(status, pct) {
  const signed = formatSignedPct(pct).replace(/[()]/g, '');
  return `${status || 'No data'}${signed ? ` ${signed}` : ''}`;
}

export const DETAILS_HINT = 'Click for details';

/**
 * The one hover layout every live map uses: a name, an optional subtitle, and
 * a few label/value rows. Kept narrow so it stays readable at large display
 * scaling; the full card opens in the map's detail drawer on click.
 * rows: [{ label, value, color? }]
 */
export function compactHoverHtml({ title, subtitle = '', accent = '', rows = [], hint = DETAILS_HINT }) {
  const rowHtml = rows
    .filter((row) => row && row.value !== undefined && row.value !== null && row.value !== '')
    .map(
      (row) => `<div style="display:flex;justify-content:space-between;gap:10px;font-size:11px;line-height:1.45;">
        <span style="color:#666;">${esc(row.label)}</span>
        <span style="font-weight:700;color:${row.color || '#111'};text-align:right;">${esc(row.value)}</span>
      </div>`
    )
    .join('');
  return `<div style="font-family:${FONT};min-width:150px;max-width:230px;border-radius:6px;overflow:hidden;background:#fff;box-shadow:0 1px 6px rgba(0,0,0,0.25);">
    ${accent ? `<div style="background:${accent};height:3px;"></div>` : ''}
    <div style="padding:6px 9px 7px;">
      <div style="font-size:12px;font-weight:700;color:#111;line-height:1.25;white-space:normal;overflow-wrap:break-word;">${esc(title)}</div>
      ${subtitle ? `<div style="font-size:10px;color:#888;margin-top:1px;white-space:normal;">${esc(subtitle)}</div>` : ''}
      ${rowHtml ? `<div style="margin-top:4px;">${rowHtml}</div>` : ''}
      ${hint ? `<div style="margin-top:4px;font-size:10px;color:#1a6fb5;font-weight:600;">${esc(hint)}</div>` : ''}
    </div>
  </div>`;
}

/** 2020–2026 K-12 population change, labeled from the snapshot vintages. */
export function historicalPopulationFields(meta, properties = {}) {
  const comparison = meta?.comparisonYear || properties.comparisonYear || '2020';
  const esriYear = meta?.esriYear || properties.esriYear || '2026';
  const start = String(comparison).slice(0, 4) || '2020';
  return {
    historicalLabel: `Historical (${start}→${esriYear})`,
    historicalStatus: properties.k12TrendStatus,
    historicalPct: properties.k12ChangeComparison,
  };
}

/**
 * Compact district/county hover, built from the same card fields as
 * `districtDetailHtml`. `highlight` ({ label, value, color }) adds the map's
 * headline metric (e.g. opportunity score) to both; `hoverHighlight` only to
 * the hover, for metrics the full card already shows as a tile. `hoverRows`
 * ([{ label, value, color? }]) replaces the default population + trend rows
 * entirely, for maps whose hover should follow a "color polygons by" choice
 * (see StateDynamicsMap's dynamicsHoverRows).
 */
export function districtHoverHtml({
  title,
  subtitle = 'School District',
  fillColor,
  k12Label,
  k12Value,
  trendStatus,
  trendPct,
  highlight = null,
  hoverHighlight = null,
  hoverRows = null,
}) {
  const rows =
    hoverRows ||
    [
      { label: k12Label, value: k12Value },
      { label: 'Projected trend', value: shortTrend(trendStatus, trendPct), color: trendTextColor(trendStatus) },
    ];
  return compactHoverHtml({
    title,
    subtitle,
    accent: fillColor,
    rows: [hoverHighlight || highlight, ...rows],
  });
}

/** Full district/county card for the detail drawer. */
export function districtDetailHtml({
  title,
  subtitle = 'School District',
  fillColor,
  k12Label,
  k12Value,
  historicalLabel,
  historicalStatus,
  historicalPct,
  trendLabel,
  trendStatus,
  trendPct,
  pctHispanic,
  pctWhite,
  pctNativeAmerican,
  pctHouseholdsBelow50k,
  assessmentHeading,
  elaValue,
  elaVs,
  mathValue,
  mathVs,
  extraTiles = '',
  charterCount,
  includeAssessments = true,
  highlight = null,
}) {
  const highlightTile = highlight
    ? tile(highlight.label, highlight.value, null, { span2: true, valueColor: highlight.color || '#111' })
    : '';
  const count = Number(charterCount) || 0;
  const charterLine =
    count === 1 ? '1 charter school located here' : `${count} charter schools located here`;
  const populationRow = historicalLabel
    ? `${tile(k12Label, k12Value)}${tile(historicalLabel, trendLine(historicalStatus, historicalPct), null, { valueColor: trendTextColor(historicalStatus) })}`
    : tile(k12Label, k12Value, null, { span2: true });
  const assessmentBlock = includeAssessments
    ? `<div style="font-size:10px;font-weight:600;color:#555;margin:10px 0 6px;">${esc(assessmentHeading)}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
        ${tile('ELA % met standard', elaValue, elaVs)}
        ${tile('Math % met standard', mathValue, mathVs)}
      </div>`
    : '';
  return `<div style="font-family:${FONT};width:100%;box-sizing:border-box;background:#fff;">
    <div style="background:${fillColor};height:4px;"></div>
    <div style="padding:12px 14px 14px;">
      <div style="font-size:15px;font-weight:700;color:#111;margin-bottom:2px;line-height:1.3;white-space:normal;overflow-wrap:break-word;">${esc(title)}</div>
      <div style="font-size:10px;color:#999;margin-bottom:10px;">${esc(subtitle)}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
        ${highlightTile}
        ${populationRow}
        ${tile(trendLabel, trendLine(trendStatus, trendPct), null, { span2: true, valueColor: trendTextColor(trendStatus) })}
        ${extraTiles}
        ${tile('% Hispanic', formatPct(pctHispanic))}
        ${tile('% White', formatPct(pctWhite))}
        ${tile('% Native American', formatPct(pctNativeAmerican))}
        ${tile('% HH below $50k', formatPct(pctHouseholdsBelow50k))}
      </div>
      ${assessmentBlock}
      <div style="margin-top:8px;font-size:10px;color:#999;">${esc(charterLine)}</div>
    </div>
  </div>`;
}

export function letterGradeColor(grade) {
  const value = String(grade || '').trim().toUpperCase();
  if (value === 'A' || value === 'B') return '#2a9d2a';
  if (value === 'C') return '#b8860b';
  if (value === 'D' || value === 'F') return '#cc2222';
  return '#888888';
}

/** Academic quality line for summary cards: letter grade when the state has one. */
export function academicQualitySummary(school, { assessmentLabel = '' } = {}) {
  if (school?.letterGrade) {
    const year = school.accountabilityYear ? ` (${school.accountabilityYear})` : '';
    const index = Number.isFinite(Number(school.indexScore)) && school.indexScore !== null
      ? ` · index ${Number(school.indexScore).toFixed(1)}`
      : '';
    return {
      label: `Letter grade${year}`,
      value: `${school.letterGrade}${index}`,
      color: letterGradeColor(school.letterGrade),
    };
  }
  const ela = school?.elaProficiency;
  const math = school?.mathProficiency;
  if (ela == null && math == null) {
    return { label: 'Academic quality', value: 'Not rated', color: '#888888' };
  }
  const prefix = assessmentLabel ? `${assessmentLabel} ` : '';
  return {
    label: `${prefix}% met standard`,
    value: `ELA ${formatPct(ela)} · Math ${formatPct(math)}`,
    color: '#111',
  };
}

function sectorLabel(school) {
  if (school?.sector === 'district') return 'District school';
  if (school?.sector === 'charter' || school?.isCharter) return 'Charter';
  return 'Charter';
}

/**
 * Default hover for every school pin: name, sector and network, enrollment
 * with trend, and academic quality. `risk` (tier label + color) is passed
 * only on the enrollment-risk map.
 */
export function schoolSummaryHoverHtml(school, { assessmentLabel = '', risk = null } = {}) {
  const status = school?.enrollmentTrendStatus;
  const isNew = school?.enrollmentComparison === null || school?.enrollmentComparison === undefined;
  const network = school?.network || school?.districtName || school?.schoolDistrictName || '';
  const quality = academicQualitySummary(school, { assessmentLabel });
  return compactHoverHtml({
    title: school?.schoolName,
    subtitle: `${sectorLabel(school)}${network ? ` · ${network}` : ''}`,
    rows: [
      { label: 'Enrollment', value: formatInt(school?.enrollment) },
      {
        label: 'Trend',
        value: isNew ? 'New school' : shortTrend(status, school?.enrollmentChange),
        color: isNew ? '#555' : trendTextColor(status),
      },
      school?.letterGrade
        ? { label: 'Letter grade', value: school.letterGrade, color: quality.color }
        : { label: 'Quality', value: quality.value, color: quality.color },
      risk ? { label: 'Enrollment risk', value: risk.tierLabel || 'Not scored', color: risk.tierColor || '#111' } : null,
    ],
  });
}

export function charterHoverHtml(school, options) {
  return schoolSummaryHoverHtml(school, options);
}

function badge(text, kind) {
  if (kind === 'charter') {
    return `<span style="font-size:10px;font-weight:600;color:#fff;background:#1a6fb5;border-radius:3px;padding:2px 7px;text-transform:uppercase;">${esc(text)}</span>`;
  }
  if (kind === 'state') {
    return `<span style="font-size:10px;color:#1a6fb5;background:#e8f1fa;border-radius:3px;padding:2px 7px;">${esc(text)}</span>`;
  }
  return `<span style="font-size:10px;color:#666;background:#f0f0f0;border-radius:3px;padding:2px 7px;">${esc(text)}</span>`;
}

export function charterPopupHtml(school) {
  const pinStatus = school.enrollmentProjectionStatus;
  const barColor =
    pinStatus === 'Growing'
      ? '#2a9d2a'
      : pinStatus === 'Declining'
        ? '#cc2222'
        : pinStatus === 'Stable'
          ? '#e6c200'
          : '#aaaaaa';
  const address = formatAddress(school);
  const badges = [badge('Charter', 'charter')];
  if (isYes(school.steadyState)) badges.push(badge('Steady State', 'state'));
  else if (isYes(school.phaseInOut)) badges.push(badge('Phasing In/Out', 'state'));
  const enrollLabel = school.currentYear ? `Enrollment ${school.currentYear}` : 'Enrollment';
  const histLabel = school.comparisonYear && school.currentYear
    ? `Historical Trend (${school.comparisonYear} → ${school.currentYear})`
    : 'Historical Trend';
  const proj = `${school.enrollmentProjectionStatus || 'N/A'} ${formatSignedPct(school.enrollmentChangeFuture)}`.trim();
  const hist = `${school.enrollmentTrendStatus || 'N/A'} ${formatSignedPct(school.enrollmentChange)}`.trim();
  const elaVs = vsStateLabel(school.elaProficiencyVsStateNonFrl);
  const mathVs = vsStateLabel(school.mathProficiencyVsStateNonFrl);
  return `<div style="font-family:${FONT};width:100%;box-sizing:border-box;">
    <div style="background:${barColor};height:4px;"></div>
    <div style="padding:12px 14px 14px;">
      <div style="font-size:15px;font-weight:700;color:#111;line-height:1.3;margin-bottom:3px;">${esc(school.schoolName)}</div>
      ${address ? `<div style="font-size:11px;color:#999;margin-bottom:9px;">${esc(address)}</div>` : ''}
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px;">${badges.join('')}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:8px;">
        ${tile(enrollLabel, formatInt(school.enrollment))}
        ${tile('Projected Trend', proj, null, { valueColor: trendTextColor(school.enrollmentProjectionStatus) })}
        ${tile(histLabel, hist, null, { span2: true, valueColor: trendTextColor(school.enrollmentTrendStatus) })}
        ${tile('Grades offered', formatGrades(school.gradesOffered), null, { span2: true })}
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
        ${tile('ELA % met standard', formatPct(school.elaProficiency, 0), elaVs)}
        ${tile('Math % met standard', formatPct(school.mathProficiency, 0), mathVs)}
      </div>
    </div>
  </div>`;
}

function riskBlock(risk) {
  const points = Number.isFinite(Number(risk.points)) ? `${Math.round(risk.points)} / 100` : 'Not scored';
  const entryLabel = `Entry grade (${entryGradeName(risk.entryGrade) || 'N/A'}) vs peak${risk.entryPeakYear ? ` ${risk.entryPeakYear}` : ''}`;
  const entryValue = formatSignedPct(risk.pctFromEntryPeak).replace(/[()]/g, '') || 'N/A';
  const matLabel = `Cohort matriculation${risk.matriculationYear ? ` (${risk.matriculationYear})` : ''}`;
  return `<div style="font-size:10px;font-weight:600;color:#555;margin:10px 0 6px;">Enrollment risk (Risk Calculator, default weights)</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:8px;">
        ${tile('Overall risk', `${risk.tierLabel || 'Not scored'} · ${points}`, null, { span2: true, valueColor: risk.tierColor || '#111' })}
        ${tile(entryLabel, entryValue, null, { valueColor: Number(risk.pctFromEntryPeak) <= -0.05 ? '#cc2222' : '#111' })}
        ${tile(matLabel, formatPct(risk.matriculationRate), null, { valueColor: Number(risk.matriculationRate) < 0.9 ? '#cc2222' : '#111' })}
      </div>`;
}

function formatCounty(county) {
  const name = (county || '').trim();
  if (!name) return '';
  return /county$/i.test(name) ? name : `${name} County`;
}

/** Support-tier color: more intensive state support reads as higher risk. */
export function wsifTierColor(tier) {
  const value = String(tier || '');
  if (/tier\s*3/i.test(value)) return '#cc2222';
  if (/tier\s*[12]/i.test(value)) return '#b8860b';
  if (/foundational/i.test(value)) return '#2a9d2a';
  return '#888888';
}

function geographyRow(label, value) {
  if (!value) return '';
  return `<div style="display:flex;gap:6px;font-size:11px;line-height:1.5;">
    <span style="color:#999;min-width:62px;">${esc(label)}</span>
    <span style="color:#111;font-weight:600;overflow-wrap:break-word;">${esc(value)}</span>
  </div>`;
}

/**
 * Click pop-up for the charter enrollment-risk map. Section order follows the
 * requested spec: name, geographic area, grades, enrollment, historical trend,
 * projected trend, academic ratings (WSIF + SBAC), demographics.
 */
function entryGradeName(grade) {
  if (grade === null || grade === undefined) return '';
  return Number(grade) === 0 ? 'K' : `Grade ${grade}`;
}

/**
 * Optional `risk` adds the Risk Calculator's overall score (default weights)
 * plus entry-grade and cohort matriculation trends:
 * { points, tierLabel, tierColor, entryGrade, pctFromEntryPeak, entryPeakYear,
 *   matriculationRate, matriculationYear }.
 */
export function charterRiskPopupHtml(school, risk = null) {
  const historical = school.enrollmentTrendStatus;
  const isNew = school.enrollmentComparison === null || school.enrollmentComparison === undefined;
  const barColor =
    historical === 'Growing'
      ? '#2a9d2a'
      : historical === 'Declining'
        ? '#cc2222'
        : historical === 'Stable'
          ? '#e6c200'
          : '#aaaaaa';

  const badges = [badge('Charter', 'charter')];
  if (isYes(school.steadyState)) badges.push(badge('Steady State', 'state'));
  else if (isYes(school.phaseInOut)) badges.push(badge('Phasing In/Out', 'state'));
  if (isNew) badges.push(badge('New school', 'state'));

  const enrollLabel = school.currentYear ? `Enrollment (${school.currentYear})` : 'Enrollment';
  const histLabel =
    school.comparisonYear && school.currentYear
      ? `Historical Enrollment Trend (${school.comparisonYear} → ${school.currentYear})`
      : 'Historical Enrollment Trend';
  const histValue = isNew
    ? `New school — no ${school.comparisonYear || 'comparison-year'} enrollment`
    : trendLine(historical, school.enrollmentChange);
  const projLabel =
    school.currentYear && school.futureYear
      ? `Projected Enrollment Trend (${school.currentYear} → ${school.futureYear})`
      : 'Projected Enrollment Trend';

  const scored = school.wsifScore !== null && school.wsifScore !== undefined;
  const wsifLabel = school.wsifYear ? `WSIF Score (${school.wsifYear})` : 'WSIF Score';
  const wsifValue = scored ? `${Number(school.wsifScore).toFixed(2)} / 10` : 'Not rated';
  const wsifSub = school.wsifSupportTier
    ? { text: school.wsifSupportTier, color: wsifTierColor(school.wsifSupportTier) }
    : null;
  const elaVs = vsStateLabel(school.elaProficiencyVsStateNonFrl);
  const mathVs = vsStateLabel(school.mathProficiencyVsStateNonFrl);

  const geography = [
    geographyRow('City', school.city),
    geographyRow('County', formatCounty(school.county)),
    geographyRow('District', school.schoolDistrictName),
  ].join('');

  const demographicsYear = school.demographicsYear ? ` (${school.demographicsYear})` : '';

  return `<div style="font-family:${FONT};width:100%;box-sizing:border-box;border-radius:8px;overflow:hidden;">
    <div style="background:${barColor};height:4px;"></div>
    <div style="padding:12px 14px 14px;box-sizing:border-box;">
      <div style="font-size:15px;font-weight:700;color:#111;line-height:1.3;margin-bottom:6px;">${esc(school.schoolName)}</div>
      <div style="margin-bottom:8px;">${geography}</div>
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px;">${badges.join('')}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:8px;">
        ${tile('Grades', formatGrades(school.gradesOffered))}
        ${tile(enrollLabel, formatInt(school.enrollment))}
        ${tile(histLabel, histValue, null, { span2: true, valueColor: isNew ? '#555' : trendTextColor(historical) })}
        ${tile(projLabel, trendLine(school.enrollmentProjectionStatus, school.enrollmentChangeFuture), null, { span2: true, valueColor: trendTextColor(school.enrollmentProjectionStatus) })}
      </div>
      ${risk ? riskBlock(risk) : ''}
      <div style="font-size:10px;font-weight:600;color:#555;margin:10px 0 6px;">Academic ratings (Washington methodology)</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:8px;">
        ${tile(wsifLabel, wsifValue, wsifSub, { span2: true, valueColor: scored ? '#111' : '#888' })}
        ${tile('SBAC ELA % met standard', formatPct(school.elaProficiency, 0), elaVs)}
        ${tile('SBAC Math % met standard', formatPct(school.mathProficiency, 0), mathVs)}
      </div>
      <div style="font-size:10px;color:#999;margin:-2px 0 8px;">Assessment ratings compare to state non-low-income students.</div>
      <div style="font-size:10px;font-weight:600;color:#555;margin:10px 0 6px;">Student demographics${esc(demographicsYear)}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;">
        ${tile('% Hispanic', formatPct(school.pctHispanic))}
        ${tile('% Black', formatPct(school.pctBlack))}
        ${tile('% FRL', formatPct(school.pctFrl))}
      </div>
    </div>
  </div>`;
}

/**
 * Full click card for statewide (letter-grade) schools, district or charter:
 * geography, grades, enrollment trends, optional risk, letter grade and state
 * assessment results, and demographics.
 */
export function schoolDetailPopupHtml(school, { risk = null, assessmentLabel = '', qualityLabel = '' } = {}) {
  const historical = school.enrollmentTrendStatus;
  const isNew = school.enrollmentComparison === null || school.enrollmentComparison === undefined;
  const barColor =
    historical === 'Growing'
      ? '#2a9d2a'
      : historical === 'Declining'
        ? '#cc2222'
        : historical === 'Stable'
          ? '#e6c200'
          : '#aaaaaa';
  const isDistrict = school.sector === 'district';
  const badges = [isDistrict ? badge('District school', '') : badge('Charter', 'charter')];
  if (school.charterType) badges.push(badge(school.charterType, ''));
  if (isYes(school.steadyState)) badges.push(badge('Steady State', 'state'));
  else if (isYes(school.phaseInOut)) badges.push(badge('Phasing In/Out', 'state'));
  if (isNew) badges.push(badge('New school', 'state'));
  if (school.quality === 'quality') badges.push(badge(qualityLabel ? `Quality (${qualityLabel})` : 'Quality', 'state'));

  const enrollLabel = school.currentYear ? `Enrollment (${school.currentYear})` : 'Enrollment';
  const histLabel =
    school.comparisonYear && school.currentYear
      ? `Historical Enrollment Trend (${school.comparisonYear} → ${school.currentYear})`
      : 'Historical Enrollment Trend';
  const histValue = isNew
    ? `New school — no ${school.comparisonYear || 'comparison-year'} enrollment`
    : trendLine(historical, school.enrollmentChange);
  const projLabel = school.futureYear
    ? `Projected Enrollment Trend (→ ${school.futureYear})`
    : 'Projected Enrollment Trend (5 years)';
  const gradeYear = school.accountabilityYear ? ` (${school.accountabilityYear})` : '';
  const index = Number.isFinite(Number(school.indexScore)) && school.indexScore !== null
    ? { text: `Index score ${Number(school.indexScore).toFixed(1)}`, color: '#555' }
    : null;
  const prefix = assessmentLabel ? `${assessmentLabel} ` : '';
  const network = school.network && school.network !== school.districtName ? school.network : '';
  const geography = [
    geographyRow('City', school.city),
    geographyRow('County', formatCounty(school.county)),
    geographyRow(isDistrict ? 'District' : 'LEA', school.districtName || school.schoolDistrictName),
    geographyRow('Network', network),
  ].join('');

  return `<div style="font-family:${FONT};width:100%;box-sizing:border-box;border-radius:8px;overflow:hidden;">
    <div style="background:${barColor};height:4px;"></div>
    <div style="padding:12px 14px 14px;box-sizing:border-box;">
      <div style="font-size:15px;font-weight:700;color:#111;line-height:1.3;margin-bottom:6px;">${esc(school.schoolName)}</div>
      <div style="margin-bottom:8px;">${geography}</div>
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px;">${badges.join('')}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:8px;">
        ${tile('Grades', formatGrades(school.gradesOffered))}
        ${tile(enrollLabel, formatInt(school.enrollment))}
        ${tile(histLabel, histValue, null, { span2: true, valueColor: isNew ? '#555' : trendTextColor(historical) })}
        ${tile(projLabel, trendLine(school.enrollmentProjectionStatus, school.enrollmentChangeFuture), null, { span2: true, valueColor: trendTextColor(school.enrollmentProjectionStatus) })}
      </div>
      ${risk ? riskBlock(risk) : ''}
      <div style="font-size:10px;font-weight:600;color:#555;margin:10px 0 6px;">Academic ratings</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:8px;">
        ${tile(`Letter grade${gradeYear}`, school.letterGrade || 'Not rated', index, { span2: true, valueColor: letterGradeColor(school.letterGrade) })}
        ${tile(`${prefix}ELA % met standard`, formatPct(school.elaProficiency, 0))}
        ${tile(`${prefix}Math % met standard`, formatPct(school.mathProficiency, 0))}
      </div>
      <div style="font-size:10px;font-weight:600;color:#555;margin:10px 0 6px;">Student demographics${school.currentYear ? ` (${esc(school.currentYear)})` : ''}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
        ${tile('% Students of color', formatPct(school.pctStudentsOfColor))}
        ${tile('% Low income', formatPct(school.pctLowIncome ?? school.pctFrl))}
      </div>
    </div>
  </div>`;
}

export function yearPair(properties = {}, meta = {}) {
  return {
    current: String(properties.esriYear || meta.esriYear || '2026'),
    future: String(properties.esriFutureYear || meta.esriFutureYear || '2031'),
  };
}

function ringPopulationTiles(school, meta) {
  if (school?.population == null && school?.populationFuture == null) return '';
  const years = yearPair(school, meta);
  return `${tile(`${years.current} ring pop`, formatInt(school.population))}
      ${tile(`${years.future} ring pop`, formatInt(school.populationFuture))}`;
}

export function wardHoverHtml(properties, fillColor, meta) {
  const years = yearPair(properties, meta);
  return compactHoverHtml({
    title: `${properties?.name || 'Ward'} Ward`,
    accent: fillColor,
    rows: [
      { label: `${years.current} K-12`, value: formatInt(properties?.k12Population) },
      {
        label: 'Projected trend',
        value: shortTrend(properties?.k12ProjectionStatus, properties?.k12ChangeFuture),
        color: trendTextColor(properties?.k12ProjectionStatus),
      },
      { label: 'Charter share', value: formatPct(properties?.charterShare) },
    ],
  });
}

/** Full ward card for the detail drawer. */
export function wardDetailHtml(properties, fillColor, meta) {
  const years = yearPair(properties, meta);
  const name = properties?.name || 'Ward';
  return `<div style="font-family:${FONT};width:100%;box-sizing:border-box;background:#fff;">
    <div style="background:${fillColor};height:4px;"></div>
    <div style="padding:12px 14px 14px;">
      <div style="font-size:15px;font-weight:700;color:#111;margin-bottom:2px;">${esc(name)} Ward</div>
      <div style="font-size:10px;color:#999;margin-bottom:10px;">Newark, New Jersey</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
        ${tile(`${years.current} K-12`, formatInt(properties.k12Population))}
        ${tile(`${years.future} K-12`, formatInt(properties.k12PopulationFuture))}
        ${tile(
          `${years.current}→${years.future}`,
          trendLine(properties.k12ProjectionStatus, properties.k12ChangeFuture),
          null,
          { span2: true, valueColor: trendTextColor(properties.k12ProjectionStatus) }
        )}
        ${tile('Charter share', formatPct(properties.charterShare))}
        ${tile('% Black', formatPct(properties.pctBlack))}
        ${tile('% Hispanic', formatPct(properties.pctHispanic))}
        ${tile('% White', formatPct(properties.pctWhite))}
        ${tile('% HH below $50k', formatPct(properties.pctHouseholdsBelow50k), null, { span2: true })}
      </div>
    </div>
  </div>`;
}

export function ringHoverHtml(properties, meta) {
  const years = yearPair(properties, meta);
  return compactHoverHtml({
    title: properties?.schoolName || 'Walk ring',
    rows: [
      { label: `${years.current} ring pop`, value: formatInt(properties?.population) },
      { label: `${years.future} ring pop`, value: formatInt(properties?.populationFuture) },
    ],
    hint: '',
  });
}

export function gapHoverHtml(properties, meta) {
  const years = yearPair(properties, meta);
  const copy = gradeBandChildCopy(properties?.gradeBand || meta?.gradeBand);
  const hasPopulation = properties?.population != null;
  const trend = childPopTrendDisplay(properties?.population, properties?.populationFuture);
  return compactHoverHtml({
    title: properties?.label || 'Coverage gap',
    rows: [
      { label: 'Area', value: formatAreaMi2(properties?.areaSqMiles) },
      hasPopulation ? { label: `${years.current} ${copy.band} children`, value: formatInt(properties.population) } : null,
      hasPopulation ? { label: 'Projected trend', value: trend.text, color: trend.color } : null,
    ],
  });
}

/** Full coverage-gap card for the detail drawer. */
export function gapDetailHtml(properties, meta) {
  const years = yearPair(properties, meta);
  const copy = gradeBandChildCopy(properties?.gradeBand || meta?.gradeBand);
  const title = properties?.label || 'Coverage gap';
  const hasPopulation =
    properties?.population != null || properties?.populationFuture != null;
  const trend = childPopTrendDisplay(properties?.population, properties?.populationFuture);
  const childrenLabel = `${years.current} ${copy.band} children (${copy.ages})`;
  return `<div style="font-family:${FONT};width:100%;box-sizing:border-box;background:#fff;">
    <div style="padding:12px 14px 14px;">
      <div style="font-size:13px;font-weight:700;color:#111;margin-bottom:2px;line-height:1.3;">${esc(title)}</div>
      <div style="font-size:10px;color:#999;margin-bottom:10px;">Area: ${esc(formatAreaMi2(properties?.areaSqMiles))}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
        ${
          hasPopulation
            ? `${tile(childrenLabel, formatInt(properties.population), null, { span2: true })}
        ${tile('Child Pop Projected Trend', trend.text, null, { valueColor: trend.color })}
        ${tile(`${years.future} children`, formatInt(properties.populationFuture))}`
            : tile('Area', formatAreaMi2(properties?.areaSqMiles), null, { span2: true })
        }
      </div>
    </div>
  </div>`;
}

function ringPopulationRows(school, meta) {
  if (school?.population == null && school?.populationFuture == null) return [];
  const years = yearPair(school, meta);
  return [
    { label: `${years.current} ring pop`, value: formatInt(school.population) },
    { label: `${years.future} ring pop`, value: formatInt(school.populationFuture) },
  ];
}

export function newarkSchoolHoverHtml(school, meta) {
  const status = school?.enrollmentTrendStatus;
  return compactHoverHtml({
    title: school?.schoolName,
    subtitle: school?.sector === 'charter' ? 'Charter' : 'District',
    rows: [
      { label: 'Enrollment', value: formatInt(school?.enrollment) },
      { label: 'Trend', value: status || 'No data', color: trendTextColor(status) },
      ...ringPopulationRows(school, meta),
    ],
  });
}

export function lowQualityHoverHtml(school, meta) {
  return compactHoverHtml({
    title: school?.schoolName,
    subtitle: 'Below city in ELA and Math',
    rows: [
      { label: 'Enrollment', value: formatInt(school?.enrollment) },
      ...ringPopulationRows(school, meta),
    ],
  });
}

export function prekHoverHtml(school) {
  const ward = school?.region ? `${school.region} Ward` : 'Ward unknown';
  return compactHoverHtml({
    title: school?.schoolName,
    subtitle: `${school?.schoolType || 'Pre-K'} · ${ward}`,
    hint: '',
  });
}

export function lowQualityPopupHtml(school, meta) {
  return `<div style="font-family:${FONT};padding:12px 14px;">
    <div style="font-size:15px;font-weight:700;color:#111;margin-bottom:6px;">${esc(school?.schoolName)}</div>
    <div style="font-size:12px;color:#cc2222;font-weight:700;margin-bottom:8px;">Below city in ELA and Math</div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
      ${tile('Enrollment', formatInt(school?.enrollment), null, { span2: school?.population == null && school?.populationFuture == null })}
      ${ringPopulationTiles(school, meta)}
    </div>
  </div>`;
}

export function overlapHoverHtml(count) {
  return compactHoverHtml({
    title: `${count} overlapping catchment${count === 1 ? '' : 's'}`,
    hint: '',
  });
}

export function newarkSchoolPopupHtml(school, meta) {
  const kind = school?.sector === 'charter' ? 'charter' : '';
  const sectorLabel = school?.sector === 'charter' ? 'Charter' : 'District';
  return `<div style="font-family:${FONT};padding:12px 14px;">
    <div style="font-size:15px;font-weight:700;color:#111;margin-bottom:6px;">${esc(school?.schoolName)}</div>
    <div style="margin-bottom:8px;">${badge(sectorLabel, kind)}</div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
      ${tile('Grades', formatGrades(school?.gradesOffered))}
      ${tile('Enrollment', formatInt(school?.enrollment))}
      ${ringPopulationTiles(school, meta)}
    </div>
  </div>`;
}
